import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiError } from '@/shared/api/client'
import { integrationsApi, ik } from '@/entities/integration'
import { PageHeader } from '@/widgets/layout/ui/AppLayout'
import { Empty, SendIcon } from './kit'
import { OutgoingTab, useOpenOAuthResult } from './OutgoingTab'
import { SourcesTab } from './SourcesTab'
import { InboundTab } from './InboundTab'
import { JournalTab } from './Journal'
import s from './integrations.module.css'

type Tab = 'out' | 'sources' | 'in' | 'journal'

const TABS: { key: Tab; title: string; text: string }[] = [
  { key: 'out', title: 'Отправка в CRM', text: 'Итоги звонков в CRM' },
  { key: 'sources', title: 'Данные для бота', text: 'Объекты, цены, сделки' },
  { key: 'in', title: 'Приём заявок', text: 'Заявки → в обзвон' },
  { key: 'journal', title: 'Журнал', text: 'Все запросы за 30 дней' },
]

function readTab(): Tab {
  const t = new URLSearchParams(window.location.search).get('tab')
  return (TABS.some((x) => x.key === t) ? t : 'out') as Tab
}

export function IntegrationsPage() {
  const [tab, setTabState] = useState<Tab>(readTab)
  const [journalHook, setJournalHook] = useState(0)

  useEffect(() => {
    useOpenOAuthResult()
  }, [])

  const setTab = (t: Tab) => {
    setTabState(t)
    const u = new URL(window.location.href)
    u.searchParams.set('tab', t)
    window.history.replaceState(null, '', u)
  }

  const catalog = useQuery({ queryKey: ik.catalog, queryFn: ({ signal }) => integrationsApi.catalog(signal), staleTime: 60_000, retry: false })
  const hooks = useQuery({ queryKey: ik.webhooks, queryFn: ({ signal }) => integrationsApi.webhooks(signal), enabled: catalog.isSuccess, refetchInterval: 30_000 })
  const sources = useQuery({ queryKey: ik.sources, queryFn: ({ signal }) => integrationsApi.sources(signal), enabled: catalog.isSuccess, refetchInterval: 30_000 })
  const inbound = useQuery({ queryKey: ik.inbound, queryFn: ({ signal }) => integrationsApi.inbound(signal), enabled: catalog.isSuccess })

  const off =
    catalog.error instanceof ApiError &&
    (catalog.error.field === 'integrations' || catalog.error.status === 403 || catalog.error.status === 404)

  const counts: Record<Tab, number | undefined> = {
    out: hooks.data?.length,
    sources: sources.data?.length,
    in: inbound.data?.length,
    journal: undefined,
  }
  const failing = (hooks.data ?? []).filter((h) => h.pausedReason || h.stats.failed > 0).length

  return (
    <div className={s.page}>
      <PageHeader
        title="Интеграции"
        description="Связь бота с вашей CRM и сайтом: итоги звонков уходят к вам, заявки приходят к нам, бот перед звонком знает данные клиента."
      />

      {off ? (
        <Empty
          icon={<SendIcon />}
          title="Интеграции пока не включены"
          text="Напишите менеджеру — включим для вашей компании за пару минут."
        />
      ) : (
        <>
          <nav className={s.tabs} role="tablist">
            {TABS.map((t) => (
              <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
                className={`${s.tab} ${tab === t.key ? s.tabActive : ''}`} onClick={() => setTab(t.key)}>
                <span title={t.text}>{t.title}</span>
                {t.key === 'out' && failing > 0 ? (
                  <span className={`${s.tabCount} ${s.tabCountAlert}`} title="Есть ошибки доставки">!</span>
                ) : counts[t.key] ? (
                  <span className={s.tabCount}>{counts[t.key]}</span>
                ) : null}
              </button>
            ))}
          </nav>

          {!catalog.data ? (
            <div className={s.skeletonGrid}>
              {[0, 1, 2].map((i) => (
                <div key={i} className={s.skeleton} />
              ))}
            </div>
          ) : (
            <section className={s.section} key={tab}>
              {tab === 'out' && (
                <OutgoingTab catalog={catalog.data} hooks={hooks.data ?? []}
                  onOpenJournal={(id) => (setJournalHook(id), setTab('journal'))} />
              )}
              {tab === 'sources' && <SourcesTab catalog={catalog.data} sources={sources.data ?? []} />}
              {tab === 'in' && <InboundTab catalog={catalog.data} links={inbound.data ?? []} />}
              {tab === 'journal' && (
                <JournalTab hooks={hooks.data ?? []} inbound={inbound.data ?? []} webhookId={journalHook} setWebhookId={setJournalHook} />
              )}
            </section>
          )}
        </>
      )}
    </div>
  )
}
